// Single backend: the SPA is served by the same server that exposes /api, so all
// requests are same-origin (relative paths). In dev, Vite proxies /api → :3000.
export function getApiBase(): string {
  return "";
}

// Browser session token (fallback to cookies). Some browsers (Firefox/Safari)
// block third-party cookies, so when client and API are on different origins
// the cookie never reaches the API. After OAuth the server hands us the session
// token via the URL fragment; we store it and send it as a Bearer header.
const SESSION_TOKEN_KEY = "regs_session_token";

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string | null) {
  try {
    if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
    else localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// Read a `#st=<token>` fragment left by the OAuth callback, persist it, and
// strip it from the URL. Call once on app boot.
export function captureSessionTokenFromUrl() {
  const h = window.location.hash;
  const m = h.match(/[#&]st=([^&]+)/);
  if (m) {
    setSessionToken(decodeURIComponent(m[1]));
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", clean);
  }
}

// Админский предпросмотр «от лица роли»: выбранная роль хранится локально и
// уходит заголовком со всеми запросами. Сервер понижает доступ ТОЛЬКО для
// реальных админов — у остальных заголовок игнорируется.
const VIEW_AS_KEY = "viewAsRole";
export type ViewAsRole = { id: string; name: string };

export function getViewAsRole(): ViewAsRole | null {
  try {
    return JSON.parse(
      localStorage.getItem(VIEW_AS_KEY) ?? "null",
    ) as ViewAsRole | null;
  } catch {
    return null;
  }
}

export function setViewAsRole(v: ViewAsRole | null) {
  try {
    if (v) localStorage.setItem(VIEW_AS_KEY, JSON.stringify(v));
    else localStorage.removeItem(VIEW_AS_KEY);
  } catch {
    /* ignore */
  }
}

export function authHeaders(): Record<string, string> {
  const t = getSessionToken();
  const h: Record<string, string> = t ? { Authorization: `Bearer ${t}` } : {};
  const v = getViewAsRole();
  if (v) h["X-View-As-Role"] = v.id;
  return h;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    // Разобранное JSON-тело ответа (если было): нужно хендлерам, которым мало
    // кода и сообщения — например, retryAfterSec у 429 чата.
    public body?: unknown,
  ) {
    super(message);
  }
}

// Общая обработка неуспешного ответа: достаём человекочитаемое поле error
// (или сырой текст), бросаем ApiError с кодом и телом.
async function throwApiError(res: Response): Promise<never> {
  const text = await res.text().catch(() => "");
  let body: unknown = undefined;
  let msg = text;
  try {
    body = JSON.parse(text);
    const e = (body as { error?: unknown } | null)?.error;
    if (e != null) msg = String(e);
  } catch {
    // not JSON — keep the body as-is
  }
  throw new ApiError(res.status, msg || res.statusText, body);
}

async function parseBody<T>(res: Response): Promise<T> {
  const ct = res.headers.get("content-type") ?? "";
  return ct.includes("application/json")
    ? ((await res.json()) as T)
    : (undefined as T);
}

// Мутирующий запрос с обязательной проверкой res.ok: достаёт человекочитаемое
// поле error из JSON-ответа и бросает ApiError. Кнопки-хендлеры должны ходить
// через send(), а не через голый fetch: без проверки ok 4xx/5xx проглатывается
// и UI делает вид, что успех. authHeaders() прилагается всегда (Bearer-фолбэк
// для браузеров, где кука не доезжает).
export async function send<T = unknown>(
  path: string,
  method: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${getApiBase()}${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) await throwApiError(res);
  return parseBody<T>(res);
}

// Загрузка файла: FormData уходит multipart (Content-Type ставит браузер),
// Blob/File — сырым телом application/octet-stream. Ошибки — как у send().
export async function sendForm<T = unknown>(
  path: string,
  body: FormData | Blob,
): Promise<T> {
  const headers: Record<string, string> = { ...authHeaders() };
  if (!(body instanceof FormData))
    headers["Content-Type"] = "application/octet-stream";
  const res = await fetch(`${getApiBase()}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
    body,
  });
  if (!res.ok) await throwApiError(res);
  return parseBody<T>(res);
}

// GET не-JSON ответа (HTML-снимок, текст). Ошибки — как у send().
export async function getText(
  path: string,
  accept = "text/plain",
): Promise<string> {
  const res = await fetch(`${getApiBase()}${path}`, {
    credentials: "include",
    headers: { Accept: accept, ...authHeaders() },
  });
  if (!res.ok) await throwApiError(res);
  return res.text();
}

// Скачивание файла с авторизацией: тянем blob и кликаем по <a download>.
// Имя берём из Content-Disposition, иначе — filename.
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(`${getApiBase()}${path}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  if (!res.ok) await throwApiError(res);
  const cd = res.headers.get("Content-Disposition");
  const m = cd && /filename="?([^"]+)"?/.exec(cd);
  const name = m ? m[1] : filename;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function fetcher<T>(path: string): Promise<T> {
  const base = getApiBase();
  const res = await fetch(`${base}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...authHeaders() },
  });
  if (res.status === 401) {
    // Сессия истекла / нет авторизации — чистим токен и отправляем на логин.
    // Путь строим от BASE_URL, чтобы работал и деплой в подпапку.
    setSessionToken(null);
    const loginPath = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/login`;
    if (window.location.pathname !== loginPath) {
      window.location.href = loginPath;
    }
    throw new ApiError(401, "unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // Routes answer errors as { error: "…" } — show that message, not the raw
    // JSON, since SWR consumers render error.message straight to the user.
    let msg = text;
    try {
      const d = JSON.parse(text) as { error?: string };
      if (d?.error) msg = d.error;
    } catch {
      // not JSON — keep the body as-is
    }
    throw new ApiError(res.status, msg || res.statusText);
  }
  return (await res.json()) as T;
}

export type GscPermission =
  | "siteOwner"
  | "siteFullUser"
  | "siteRestrictedUser"
  | "siteUnverifiedUser"
  | "unknown"
  | null;

export type SiteListItem = {
  id: string;
  url: string;
  type: string;
  ignored?: boolean;
  gscPermission?: GscPermission;
  lastSyncedAt: string | null;
  createdAt?: string;
  // First appearance on Google (earliest GSC date); null if not seen yet.
  firstSeenAt?: string | null;
  statsSyncedAt?: string | null;
  inspectSyncedAt?: string | null;
  perfSyncedAt?: string | null;
  // Локаль ленда: язык + страна + чем определили (config | html | locales |
  // path | domain | manual). См. lib/locale.ts.
  localeLang?: string | null;
  localeCountry?: string | null;
  localeSource?: string | null;
  _count?: { pages: number };
};

export type SearchStat = {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

export type QueryStat = {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

export type Performance = {
  date: string;
  strategy?: string;
  performanceScore?: number | null;
  lcp?: number | null;
  inp?: number | null;
  cls?: number | null;
  score?: number | null;
};

export type Crux = {
  date: string;
  lcp?: number | null;
  inp?: number | null;
  cls?: number | null;
};

export type Page = {
  id: string;
  url: string;
  title?: string | null;
  indexStatus?: string | null;
  crawlIssues?: string | null;
  httpStatus?: number | null;
  responseTimeMs?: number | null;
  syncStatus?: string | null;
  syncError?: string | null;
  publishedAt?: string | null;
  deletedAt?: string | null;
  lastCheckedAt?: string | null;
  lastCrawledAt?: string | null;
  lastIndexNotifiedAt?: string | null;
  lastIndexNotifyType?: string | null;
  searchStats: SearchStat[];
  queryStats: QueryStat[];
  performance: Performance[];
  crux: Crux[];
};

// Сетка сайта: задана вручную на «Структуре» ("manual" — по ней же роутит шлюз
// и ресайкл), из конфига самого лендинга ("lander") и/или по регам домена в
// таблице сети ("regs"). Едет в /sites/:id/full — видна любой роли.
export type SiteNetwork = {
  id: string | null;
  name: string;
  regs: number;
  source: "lander" | "regs" | "manual";
  // Доля портфеля сети по кешу с аппрувов за всё время (0..1); null — сеть
  // неизвестна.
  portfolioShare?: number | null;
};

// Языковая версия домена: у GSC на каждую папку (`site.com/ro/`) заведено своё
// свойство, и в базе это отдельная строка Site.
export type SiteSibling = {
  id: string;
  url: string;
  localeLang: string | null;
  localeCountry: string | null;
};

export type SiteFull = SiteListItem & {
  pages: Page[];
  // Машина, на которой лежит лендинг (см. METRICS_SSH_SERVERS на сервере).
  serverId?: string | null;
  serverShort?: string | null;
  serverLabel?: string | null;
  networks?: SiteNetwork[];
  // Все языковые версии домена и какая из них сейчас показана: "all" — домен
  // целиком (по умолчанию), иначе id конкретной версии.
  siblings?: SiteSibling[];
  lang?: string;
};

export type SiteStructure = {
  config: {
    task: string | null;
    name: string | null;
    mainLang: string | null;
    htmlLang: string | null;
    integration: string | null;
    integrationType: string | null;
    template: string | null;
    languages: string[];
    fetchedAt: string;
  } | null;
  localeConfigs: {
    lang: string;
    title: string | null;
    description: string | null;
    locale: string | null;
    priceValue: string | null;
    priceCurrency: string | null;
    offer: string | null;
    fetchedAt: string;
  }[];
  // Per-locale реги-оверрайд (редактируется в UI, gateway читает поверх файла).
  localeRegFields: {
    lang: string;
    network: string | null;
    offer: string | null;
    flow: string | null;
    country: string | null;
    priceValue: string | null;
    priceCurrency: string | null;
    orderId: string | null;
    updatedAt: string;
  }[];
  keywords: { keyword: string; count: number }[];
  source: {
    repo: string;
    branch: string | null;
    template: string | null;
    deploy: string | null;
    skill: string | null;
    note: string | null;
    source: "auto" | "manual";
    updatedAt: string;
  } | null;
};

// --- SEO change tracking ---

export type TrackingAgg = {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

export type TrackingQueryCmp = {
  query: string;
  before: TrackingAgg;
  after: TrackingAgg;
  delta: TrackingAgg;
};

export type TrackingCheckpointDto = {
  id: string;
  offsetDays: number;
  dueDate: string;
  status: "pending" | "ready" | "acknowledged";
  readyAt: string | null;
  acknowledgedAt: string | null;
  result: null | {
    computedAt: string;
    window: {
      beforeStart: string;
      beforeEnd: string;
      afterStart: string;
      afterEnd: string;
    };
    page: { before: TrackingAgg; after: TrackingAgg; delta: TrackingAgg };
    queries: {
      improved: TrackingQueryCmp[];
      declined: TrackingQueryCmp[];
      appeared: TrackingQueryCmp[];
      disappeared: TrackingQueryCmp[];
    };
  };
};

// Свежесть данных Search Console по флоту: у скольких сайтов последний день
// статистики N дней назад (GET /api/summary/gsc-freshness).
export type GscFreshness = {
  today: string;
  totalSites: number;
  byDaysAgo: { daysAgo: number; sites: number }[];
  // Не в проценте: статы нет неделю (stale) или нет вовсе (noData).
  excluded: { stale: number; noData: number };
  byDate: { date: string; sites: number }[];
  // Прогноз свежих (ещё дописываемых GSC) дней по среднему 7 устоявшихся.
  // Прогноз незавершённых PT-дней по сайтам: пришло + обычный день сайта ×
  // доля суток, которую GSC ещё не отдал.
  forecast: {
    // По какой день данные GSC окончательные (у большинства сайтов).
    finalThrough?: string | null;
    days: {
      date: string;
      // Google ещё не закрыл день — данные предварительные.
      preliminary?: boolean;
      clicks: number;
      impressions: number;
      clicksForecast: number;
      impressionsForecast: number;
      // Какая доля прогноза уже пришла, %.
      arrivedPct: number;
      // Какую долю суток GSC уже должен был отдать, %.
      dayPct: number;
      sitesIn: number;
      sitesPending: number;
      // Ждём от сайтов без статы / добор у уже подгруженных, клики.
      pendingClicks: number;
      topUpClicks: number;
    }[];
  };
};

// Прогноз уникальных рег на сегодня (GET /api/regs/forecast).
export type RegsForecast = {
  date: string;
  hour: number;
  actual: number;
  expectedRest: number;
  forecast: number;
  avgDay: number;
  baseline: { from: string; to: string };
  // В 8 из 10 прошлых дней в этот час промах не больше, %.
  errorPct: number | null;
};

export type TrackingCycleItem = {
  id: string;
  status: "active" | "completed";
  changeDate: string;
  jiraUrl: string | null;
  description: string | null;
  startedAt: string;
  completedAt: string | null;
  page: { id: string; url: string };
  site: { id: string; url: string };
  checkpoints: TrackingCheckpointDto[];
  // ИИ по этой странице за цикл: цитирования (чат открыл страницу) и
  // переходы людей из чатов.
  ai?: { fetch: number; visit: number };
};

export type AiDay = {
  date: string;
  fetch: number;
  visit: number;
  gscAi: number;
  bingAi: number;
};

export type TrackingCycleFull = TrackingCycleItem & {
  stats: SearchStat[];
  ai: AiDay[];
  previousCycles: {
    id: string;
    status: string;
    changeDate: string;
    jiraUrl: string | null;
    description: string | null;
  }[];
};

export type TrackingQueriesResponse = {
  offset: number;
  baselineCovered: boolean;
  improved: TrackingQueryCmp[];
  declined: TrackingQueryCmp[];
  appeared: TrackingQueryCmp[];
  disappeared: TrackingQueryCmp[];
};

// AI keys
export type AiKeyItem = {
  id: string;
  provider: "openai" | "anthropic" | "google";
  model: string;
  label: string;
  preview: string;
  expiresAt: string | null;
  disabled: boolean;
  primary: boolean;
  lastUsedAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  failCount: number;
  createdAt: string;
  inputPricePerM?: number | null;
  outputPricePerM?: number | null;
};

export type AiUsage = {
  month: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  byModel: {
    model: string;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  }[];
};

export type AiScopeItem = {
  key: string;
  label: string;
  enabled: boolean;
};

// --- Finance ---

export type FinanceStatusKey = "approve" | "cancel" | "trash" | "wait";

export type FinanceStatusAgg = {
  count: number;
  cashUsd: number;
  shareOfMatched: number;
  // Доля от ВСЕХ рег периода — по ней сравниваются периоды (± п.п.).
  shareOfRegs: number;
};

// Тайминги статусов (GET /api/finance) — сколько лид ждал до смены статуса и
// сколько рег периода оставались в wait на КОНЕЦ периода.
export type WaitOutcomeStat = {
  count: number;
  avgHours: number | null;
  medianHours: number | null;
};

export type FinanceTiming = {
  waitAsOf: number;
  waitAsOfShare: number;
  fromHistory: boolean;
  historyCoverage: number;
  timing: {
    resolved: number;
    avgHours: number | null;
    medianHours: number | null;
    minHours: number | null;
    maxHours: number | null;
    byOutcome: Record<"approve" | "cancel" | "trash", WaitOutcomeStat>;
  };
};

export type FinanceTotals = {
  regs: number;
  matched: number;
  cashUsd: number;
  byStatus: Record<FinanceStatusKey, FinanceStatusAgg>;
};

export type FinanceDailyPoint = {
  date: string;
  // Весь кеш дня + раздельно по статусам (всё в USD).
  cashUsd: number;
  approveCash: number;
  cancelCash: number;
  trashCash: number;
  waitCash: number;
  regs: number;
  approve: number;
  cancel: number;
  trash: number;
  wait: number;
};

export type FinanceDim = "site" | "network" | "offer";

export type FinanceBreakdownRow = {
  key: string; // домен | имя сети | оффер
  cashUsd: number; // АППРУВНУТЫЙ кеш
  regs: number;
  matched: number;
  approve: number;
  cancel: number;
  trash: number;
  prev: {
    cashUsd: number;
    approve: number;
    cancel: number;
    trash: number;
  } | null;
};

export type FinanceSummary = {
  range: { from: string; to: string };
  prevRange: { from: string; to: string };
  fx: { eurUsd: number; fetchedAt: string | null };
  totals: FinanceTotals;
  prevTotals: FinanceTotals;
  timing: FinanceTiming;
  prevTiming: FinanceTiming;
  daily: FinanceDailyPoint[];
  breakdowns: Record<FinanceDim, FinanceBreakdownRow[]>;
};

// ─── Когорты по брендам (GET /api/finance/cohorts) ──────────────────────────

export type CohortMonthPoint = {
  month: string;
  monthIndex: number;
  regs: number;
  approve: number;
  cashUsd: number;
};

export type CohortGeoRow = {
  geo: string;
  domains: string[];
  regs: number;
  approve: number;
  approveRate: number;
  cashUsd: number;
  avgCheck: number;
  byMonth: CohortMonthPoint[];
};

// Вердикт бренда: hit ≥ p75 $/мес (среди брендов с деньгами), low < p25,
// mid — между, zero — денег нет.
export type BrandTier = "hit" | "mid" | "low" | "zero";

export type CohortBrandRow = {
  key: string;
  brand: string;
  brandSource: "config" | "offer" | "domain" | "unknown";
  launchMonth: string;
  firstRegAt: string;
  lastRegAt: string;
  monthsSinceLaunch: number;
  domains: string[];
  regs: number;
  approve: number;
  approveRate: number;
  cashUsd: number;
  cashPerMonth: number;
  avgCheck: number;
  tier: BrandTier;
  clicks: number;
  impressions: number;
  avgPosition: number | null;
  pagesTotal: number;
  pagesIndexed: number;
  byMonth: CohortMonthPoint[];
  geos: CohortGeoRow[];
  domainsStat: CohortDomainRow[];
  networks: string[];
};

// Ленд бренда со статой (раскрытие товара в когортной сетке).
export type CohortDomainRow = {
  domain: string;
  geos: string[];
  network: string | null;
  regs: number;
  approve: number;
  cashUsd: number;
  clicks: number;
  impressions: number;
  avgPosition: number | null;
};

export type CohortTotals = {
  brands: number;
  domains: number;
  regs: number;
  approve: number;
  cashUsd: number;
  avgCheck: number;
  approveRate: number;
};

export type CohortsResult = {
  generatedAt: string;
  eurUsd: number;
  range: { from: string | null; to: string | null };
  totals: CohortTotals;
  brands: CohortBrandRow[];
  tierThresholds: { p25: number; p75: number };
  unmappedRegs: number;
};

// ─── Жизненный цикл лендеров (GET /api/finance/site-lifecycle) ──────────────

export type LifecycleStats = {
  today: string;
  launched: { thisMonth: number; prevMonth: number; last90: number };
  timeToLaunch: {
    avgDaysToImpressions: number | null;
    medianDaysToImpressions: number | null;
    avgDaysToFirstClick: number | null;
    sample: number;
  };
  alive: { count: number; avgAgeDays: number | null };
  dead: {
    count: number;
    avgLifetimeDays: number | null;
    medianLifetimeDays: number | null;
    recent: {
      id: string;
      url: string;
      lastSeen: string;
      lifetimeDays: number;
    }[];
  };
  neverLaunched: number;
};

// ─── Ставки офферов (GET /api/offers/rates) ─────────────────────────────────

export type OfferRateRow = {
  network: string;
  networkLabel: string;
  externalId: string;
  offerName: string;
  product: string | null;
  country: string | null;
  available: boolean;
  inCatalog: boolean;
  networkRate: number | null;
  networkCurrency: string | null;
  networkRateUsd: number | null;
  actualPayout: number | null;
  actualCurrency: string | null;
  actualPayoutUsd: number | null;
  diffPct: number | null;
  geoExact: boolean;
  regs: number;
  approve: number;
  cashUsd: number;
  lastRegAt: string | null;
};

export type OfferRatesResult = { eurUsd: number; rows: OfferRateRow[] };
